import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react"
import { personalInfo } from "@/config/personal"
import { sunPosition, themeForSun, type Theme } from "@/components/sky/sky"

/** How often the theme re-checks the sun. The sun moves ~0.25° a minute. */
const THEME_REFRESH_MS = 60_000
/** Length of the day/night color fade; matches `[data-theme-fading]` in index.css. */
const THEME_FADE_MS = 1200

interface SkyClock {
  /** The moment the sky shows: real time, or a fixed one when pinned. */
  readonly now: () => Date
  /** Light while the Bangkok sun is up, dark after civil twilight. */
  readonly theme: Theme
}

const SkyClockContext = createContext<SkyClock | null>(null)

const realNow = () => new Date()

/** The site theme for the sky over home at `date`. */
export function themeAt(date: Date): Theme {
  const { lat, lon } = personalInfo.coordinates
  return themeForSun(sunPosition(date, lat, lon).elevation)
}

/**
 * One clock for everything that follows the sky over Bangkok: the top sky
 * scene and the page theme. `at` pins it to a fixed moment (ms since epoch).
 * The theme is written to `<html data-theme>` so every token follows it.
 */
export function SkyClockProvider({
  at,
  children,
}: {
  readonly at?: number
  readonly children: ReactNode
}) {
  const now = useMemo(() => (at === undefined ? realNow : () => new Date(at)), [at])
  const [liveTheme, setLiveTheme] = useState<Theme>(() => themeAt(new Date()))
  const theme = at === undefined ? liveTheme : themeAt(new Date(at))

  // Re-check the real sun each minute.
  useEffect(() => {
    if (at !== undefined) return
    const id = setInterval(() => setLiveTheme(themeAt(new Date())), THEME_REFRESH_MS)
    return () => clearInterval(id)
  }, [at])

  // Sync the theme onto the document, outside React's tree. A change after
  // the first paint fades every color for THEME_FADE_MS; the first one is
  // instant (index.html already guessed the theme before React loaded).
  useEffect(() => {
    const root = document.documentElement
    const changing = root.dataset.theme !== undefined && root.dataset.theme !== theme
    if (changing) root.dataset.themeFading = ""
    root.dataset.theme = theme
    root.style.colorScheme = theme
    if (!changing) return
    const id = setTimeout(() => delete root.dataset.themeFading, THEME_FADE_MS)
    return () => {
      clearTimeout(id)
      delete root.dataset.themeFading
    }
  }, [theme])

  const value = useMemo((): SkyClock => ({ now, theme }), [now, theme])
  return <SkyClockContext.Provider value={value}>{children}</SkyClockContext.Provider>
}

export function useSkyClock(): SkyClock {
  const value = useContext(SkyClockContext)
  if (!value) throw new Error("useSkyClock must be used inside SkyClockProvider")
  return value
}
